/**
 * TabOverrideEditor — Admin component for configuring per-tab permissions
 *
 * Displays a card per module with a 3-position toggle (full/view/none) per tab.
 * Modules without known tabs show "Pas de filtrage par onglet".
 */

import React from 'react';
import { Shield, Eye, EyeOff } from 'lucide-react';

// Registry of modules and their known tabs
const MODULE_TAB_REGISTRY = {
  insurance: [
    { id: 'catalog', label: 'Catalogue' },
    { id: 'intermediaries', label: 'Intermédiaires' },
    { id: 'contracts', label: 'Contrats' },
    { id: 'coverage', label: 'Garanties' },
    { id: 'vehicle_insurance', label: 'Véhicules Assurés' },
    { id: 'claims', label: 'Sinistres' },
  ],
  reports: [
    { id: 'conformite', label: 'Conformité' },
    { id: 'couts', label: 'Coûts' },
    { id: 'risques', label: 'Risques' },
    { id: 'synthese_hebdo', label: 'Synthèse hebdo' },
    { id: 'synthese', label: 'Synthèse annuelle' },
  ],
  fuel: [
    { id: 'logs', label: 'Pleins' },
    { id: 'cards', label: 'Cartes' },
  ],
  vehicles: [
    { id: 'list', label: 'Liste' },
    { id: 'map', label: 'Carte' },
  ],
  admin: [
    { id: 'pipeline', label: 'Pipeline' },
    { id: 'organizations', label: 'Organisations' },
    { id: 'sites', label: 'Sites' },
    { id: 'users', label: 'Utilisateurs' },
    { id: 'intermediaries', label: 'Intermédiaires' },
    { id: 'trips', label: 'Trajets' },
    { id: 'subscriptions', label: 'Rapports Prog.' },
  ],
  suppliers: [
    { id: 'directory', label: 'Annuaire' },
    { id: 'contracts', label: 'Contrats' },
    { id: 'performance', label: 'Performance' },
  ],
  costs: [
    { id: 'overview', label: 'Vue d\'ensemble' },
    { id: 'breakdown', label: 'Répartition TCO' },
    { id: 'vehicles', label: 'Par Véhicule' },
    { id: 'trends', label: 'Évolution' },
    { id: 'alerts', label: 'Alertes' },
  ],
  compliance: [
    { id: 'overview', label: 'Vue d\'ensemble' },
    { id: 'privacy-mode', label: 'Mode Privé' },
    { id: 'consent', label: 'Consentements' },
    { id: 'data-requests', label: 'Demandes RGPD' },
    { id: 'retention', label: 'Conservation' },
    { id: 'scoring', label: 'Score Conduite' },
    { id: 'country-rules', label: 'Règles Pays' },
    { id: 'audit', label: 'Journal d\'audit' },
    { id: 'notices', label: 'Avis Confidentialité' },
  ],
};

const PERM_LEVELS = [
  { value: 'full', label: 'Complet', icon: Shield, color: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  { value: 'view', label: 'Lecture', icon: Eye, color: 'bg-amber-100 text-amber-700 border-amber-300' },
  { value: 'none', label: 'Masqué', icon: EyeOff, color: 'bg-red-100 text-red-700 border-red-300' },
];

export function TabOverrideEditor({ tabOverrides = {}, onChange, selectedModules = [] }) {
  const handlePermChange = (moduleId, tabId, newPerm) => {
    const updated = { ...tabOverrides };

    if (newPerm === 'full') {
      // 'full' is the default — remove override to keep JSON clean
      if (updated[moduleId]) {
        const { [tabId]: _, ...rest } = updated[moduleId];
        if (Object.keys(rest).length === 0) {
          delete updated[moduleId];
        } else {
          updated[moduleId] = rest;
        }
      }
    } else {
      if (!updated[moduleId]) updated[moduleId] = {};
      updated[moduleId][tabId] = newPerm;
    }

    onChange(updated);
  };

  const getTabPerm = (moduleId, tabId) => {
    return tabOverrides?.[moduleId]?.[tabId] || 'full';
  };

  // Only show modules that are in selectedModules AND have known tabs
  const modulesWithTabs = selectedModules.filter(m => MODULE_TAB_REGISTRY[m]);
  const modulesWithoutTabs = selectedModules.filter(m => !MODULE_TAB_REGISTRY[m]);

  return (
    <div className="space-y-4">
      {modulesWithTabs.length === 0 && (
        <p className="text-sm text-gray-500 dark:text-slate-400 italic">
          Aucun module sélectionné ne possède de filtrage par onglet.
        </p>
      )}

      {modulesWithTabs.map(moduleId => {
        const tabs = MODULE_TAB_REGISTRY[moduleId];
        return (
          <div key={moduleId} className="border border-gray-200 dark:border-slate-600 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-3 capitalize">
              {moduleId}
            </h4>
            <div className="space-y-2">
              {tabs.map(tab => {
                const currentPerm = getTabPerm(moduleId, tab.id);
                return (
                  <div key={tab.id} className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-slate-400">{tab.label}</span>
                    <div className="flex gap-1">
                      {PERM_LEVELS.map(level => {
                        const isActive = currentPerm === level.value;
                        const Icon = level.icon;
                        return (
                          <button
                            key={level.value}
                            type="button"
                            onClick={() => handlePermChange(moduleId, tab.id, level.value)}
                            className={`px-2 py-1 text-xs rounded border flex items-center gap-1 transition-colors ${
                              isActive
                                ? level.color
                                : 'bg-gray-50 dark:bg-slate-700 text-gray-400 dark:text-slate-500 border-gray-200 dark:border-slate-600 hover:bg-gray-100 dark:hover:bg-slate-600'
                            }`}
                            title={level.label}
                          >
                            <Icon size={12} />
                            {level.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {modulesWithoutTabs.length > 0 && (
        <div className="text-xs text-gray-400 dark:text-slate-500 mt-2">
          {modulesWithoutTabs.length} module(s) sans filtrage par onglet :
          {' '}{modulesWithoutTabs.join(', ')}
        </div>
      )}
    </div>
  );
}

export default TabOverrideEditor;
